import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { TransportFiltersService } from './transport-filters.service';
import {
  TransportFiltersInitialState,
  TransportFiltersLoad,
  TransportFiltersUpdate,
} from './transport-filters.actions';
import { TransportFilterName } from '../constants';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface TransportFiltersStateModel {
  items: TransportFilterName[];
  loaded: boolean;
}

const initialState: TransportFiltersStateModel = { items: [], loaded: false };

@State<TransportFiltersStateModel>({
  name: 'transportFilters',
  defaults: initialState,
})
@Injectable()
export class TransportFiltersState {
  constructor(
    private transportFiltersService: TransportFiltersService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: TransportFiltersStateModel): TransportFiltersStateModel {
    return state;
  }

  @Selector()
  static getItems(state: TransportFiltersStateModel): TransportFilterName[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: TransportFiltersStateModel): boolean {
    return state.loaded;
  }

  @Action(TransportFiltersLoad)
  getTransportFilters(
    ctx: StateContext<TransportFiltersStateModel>,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportFiltersService.getTransportFilters().pipe(
      map((filters: TransportFilterName[]) => {
        ctx.patchState({ items: filters, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportFiltersUpdate)
  updateTransportFilters(
    ctx: StateContext<TransportFiltersStateModel>,
    { payload }: TransportFiltersUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.transportFiltersService.updateTransportFilters(payload).pipe(
      map((filters: TransportFilterName[]) => {
        ctx.patchState({ items: filters, loaded: true });
        this.notificationService.success('api.transportFilters.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(TransportFiltersInitialState)
  setInitialState(ctx: StateContext<TransportFiltersInitialState>): void {
    ctx.setState(initialState);
  }
}
